import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Grid } from "@mui/material";
import TextField from "../common/TextField";
import Button from "../common/Button";
import Table from "../common/tableWithBackEndPagination";
import ConfirmDialoag from "../common/ConfirmDialoag";
import CustomDialog from "../common/Dialog";
import Checkbox from "../common/Checkbox";
import Loader from "../../Layout/Loader";
import ExportImportMenu from "../common/ExportImportMenu";
import LoginAsUser from "../../Helpers/loginAsUser";
import { checkPrivileges, PaginationConfig } from "../../Helpers";
import { getUsers } from "../../Actions/Users";
import { parentUsersLimit } from "../../Actions/Users";
import { parentUnitsLimit } from "../../Actions/Devices";
import withRoutes from "../../HOC/HocRoutes";
import ContractTypeTabs from "../identity/ContractTypeTabs";
import { contractTypeFromTab } from "../identity/formDefaults";
import { replaceHtmlEntities } from "../identity/formatters";
import { setPagination, userRowDefinition } from "../identity/tables";
import SearchDropdown from "./SearchDropdown";
import UsersFilterPanel from "./ListFilters";
import CreateUpdateForm from "./CreateUpdateForm";
import UserDetailView from "./UserDetailView";
import ChangeContractModal from "./changeContractModal";
import { ViewStatistics } from "./statistics";
import {
  activateUser,
  countDevices,
  countUsers,
  deleteUser,
  disableUser,
  enableUser,
  handleIdentityError,
  listAccountAdmins,
  listUsers,
  mapUsersForTable,
  migrateUser,
} from "./usersApi";

const buildFilterQuery = (map, prefix) =>
  Object.entries(map || {})
    .filter(([, checked]) => checked)
    .map(([key]) => `&${prefix}=${key}`)
    .join("");

const buildCombinedFilterQuery = (statusFilter, statusRoleFilter) =>
  `${buildFilterQuery(statusFilter, "userTypes")}${buildFilterQuery(
    statusRoleFilter,
    "roleId",
  )}`;

const UsersListInner = (props) => {
  const dispatch = useDispatch();
  const logInUser = useSelector((state) => state.logInUsers);
  const roles = useSelector((state) => state.roles);
  const themecolors = useSelector((state) => state.themecolors);
  const ServerSetting = useSelector((state) => state.ServerSetting);
  const parentUserLimits = useSelector((state) => state.parentUserLimits);
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const filterFetchTimer = useRef(null);
  const searchTimer = useRef(null);

  const [mode, setMode] = useState("list");
  const [tabValue, setTabValue] = useState("maintenance");
  const [searchInput, setSearchInput] = useState("");
  const [listState, setListState] = useState({
    ...PaginationConfig,
    tableData: [],
    pagData: null,
    loading: false,
    extraQuery: "",
    cnValue: null,
  });
  const [selectedUser, setSelectedUser] = useState("");
  const [createdFlags, setCreatedFlags] = useState({ spCreated: false, aaCreated: false });
  const [filters, setFilters] = useState({
    open: false,
    statusFilter: {},
    statusRoleFilter: {},
    countChecked: 0,
    countCheckedRole: 0,
    anchorEl: null,
    anchorElRole: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [childStatus, setChildStatus] = useState({ open: false, mode: false, isSubmit: false });
  const [loginUser, setLoginUser] = useState(null);
  const [loginAsKey, setLoginAsKey] = useState(0);
  const [migrate, setMigrate] = useState({
    parentItemId: "",
    childItemId: "",
    userListType3: [],
    confirm: false,
  });

  const rowDefinition = useMemo(
    () => userRowDefinition(props.translate),
    [props.translate],
  );

  const loadUsers = useCallback(async () => {
    if (!logInUser?.id) return;
    setListState((prev) => ({ ...prev, loading: true }));
    try {
      const result = await listUsers({
        userId: logInUser.id,
        page: listState.page,
        pageSize: listState.pageSize,
        search: listState.itemSearch,
        tab: tabValue,
        extraQuery: listState.extraQuery,
        accountId: listState.cnValue?.id,
      });
      const tableData = mapUsersForTable(result.data, rolesRef.current, logInUser);
      dispatch(getUsers(tableData));
      setListState((prev) => ({
        ...prev,
        tableData,
        pagData: result,
        loading: false,
      }));
    } catch (error) {
      toast.error(handleIdentityError(error, props.translate));
      setListState((prev) => ({ ...prev, loading: false, tableData: [], pagData: null }));
    }
  }, [
    logInUser,
    listState.page,
    listState.pageSize,
    listState.itemSearch,
    listState.extraQuery,
    listState.cnValue,
    tabValue,
    dispatch,
    props.translate,
  ]);

  useEffect(() => {
    if (mode === "list" && logInUser?.id) loadUsers();
  }, [
    mode,
    logInUser?.id,
    listState.page,
    listState.pageSize,
    listState.itemSearch,
    listState.extraQuery,
    listState.cnValue,
    tabValue,
  ]);

  useEffect(() => {
    if (!logInUser?.id) return;
    countUsers(logInUser.id)
      .then((data) => dispatch(parentUsersLimit(data)))
      .catch(() => {});
    countDevices(logInUser.id)
      .then((data) => dispatch(parentUnitsLimit(data)))
      .catch(() => {});
  }, [logInUser, dispatch]);

  useEffect(
    () => () => {
      clearTimeout(filterFetchTimer.current);
      clearTimeout(searchTimer.current);
    },
    [],
  );

  const goList = () => {
    setMode("list");
    setSelectedUser("");
    setCreatedFlags({ spCreated: false, aaCreated: false });
  };

  const pickRow = (selected) => (Array.isArray(selected) ? selected[0] : selected);

  const scheduleFilterFetch = useCallback((statusFilter, statusRoleFilter) => {
    const extraQuery = buildCombinedFilterQuery(statusFilter, statusRoleFilter);
    clearTimeout(filterFetchTimer.current);
    filterFetchTimer.current = setTimeout(() => {
      setListState((prev) => {
        if (prev.extraQuery === extraQuery && prev.page === 1) return prev;
        return { ...prev, extraQuery, page: 1 };
      });
    }, 350);
  }, []);

  const handleUserTypeFilterChange = useCallback(
    (name, event) => {
      setFilters((prev) => {
        const statusFilter = {
          ...prev.statusFilter,
          [name]: event.target.checked,
        };
        scheduleFilterFetch(statusFilter, prev.statusRoleFilter);
        return {
          ...prev,
          statusFilter,
          countChecked: Object.values(statusFilter).filter(Boolean).length,
        };
      });
    },
    [scheduleFilterFetch],
  );

  const handleRoleFilterChange = useCallback(
    (name, event) => {
      setFilters((prev) => {
        const statusRoleFilter = {
          ...prev.statusRoleFilter,
          [name]: event.target.checked,
        };
        scheduleFilterFetch(prev.statusFilter, statusRoleFilter);
        return {
          ...prev,
          statusRoleFilter,
          countCheckedRole: Object.values(statusRoleFilter).filter(Boolean).length,
        };
      });
    },
    [scheduleFilterFetch],
  );

  const clearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      statusFilter: {},
      statusRoleFilter: {},
      countChecked: 0,
      countCheckedRole: 0,
      anchorEl: null,
      anchorElRole: null,
    }));
    clearTimeout(filterFetchTimer.current);
    setListState((prev) => {
      if (!prev.extraQuery && prev.page === 1) return prev;
      return { ...prev, extraQuery: "", page: 1 };
    });
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setListState((prev) => ({ ...prev, itemSearch: value, page: 1 }));
    }, 400);
  }, []);

  const handleTabChange = useCallback((tab) => {
    startTransition(() => {
      setTabValue(tab);
      setListState((prev) => ({ ...prev, page: 1, pageSize: 20, cnValue: null }));
    });
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {mode === "create" && (
        <CreateUpdateForm
          translate={props.translate}
          onCancel={goList}
          onCreated={(created) => {
            const user = created?.user || created;
            if (user?.userType === 3 || user?.userType === 4) {
              setSelectedUser(user);
              setCreatedFlags({
                aaCreated: user.userType === 3,
                spCreated: user.userType === 4,
              });
              setMode("detail");
            } else {
              goList();
            }
          }}
        />
      )}

      {mode === "detail" && selectedUser && (
        <UserDetailView
          translate={props.translate}
          defaultGarageOptions={[]}
          spCreated={createdFlags.spCreated}
          aaCreated={createdFlags.aaCreated}
          tableData={listState.tableData}
          pagination={listState.pagData}
          getMoreUsers={loadUsers}
          setPagination={(data) => setPagination(data, listState.pageSize, listState.tableData.length)}
          handleChangePage={(page) => setListState((prev) => ({ ...prev, page }))}
          handleChangeRowsPerPage={(pageSize) =>
            setListState((prev) => ({ ...prev, pageSize, page: 1 }))
          }
          selectItemName={selectedUser.name}
          selectItemId={selectedUser}
          onClose={goList}
          usersLimit={selectedUser.userLimit}
          deviceLimit={selectedUser.deviceLimit}
          replaceHtmlEntities={replaceHtmlEntities}
        />
      )}

      {mode === "changeContract" && selectedUser && (
        <ChangeContractModal
          translate={props.translate}
          logInUser={logInUser}
          roles={roles}
          changeContractOption
          formSubmit={() => {
            if (migrate.childItemId?.id) {
              setMigrate((prev) => ({ ...prev, confirm: true }));
            }
          }}
          modalControle={goList}
          buttonText={props.translate("migrate")}
          userType={logInUser.userType}
          handleChangeContractform={(name, value) =>
            setMigrate((prev) => ({
              ...prev,
              childItemId: value || "",
              parentItemId: prev.parentItemId || selectedUser.id,
            }))
          }
          isVisableUserBtn={Boolean(migrate.childItemId?.id)}
          openUserMigrateDialog={() => setMigrate((prev) => ({ ...prev, confirm: true }))}
          closeUserMigrateDialog={() => setMigrate((prev) => ({ ...prev, confirm: false }))}
          handleUserMigrate={async () => {
            try {
              await migrateUser(migrate.childItemId?.id, migrate.parentItemId);
              toast.success("User Migrated successfully");
              setMigrate({ parentItemId: "", childItemId: "", userListType3: [], confirm: false });
              goList();
            } catch (error) {
              setMigrate((prev) => ({ ...prev, confirm: false }));
              toast.error(handleIdentityError(error, props.translate));
            }
          }}
          loadNextPage={() => {}}
          userListType3={migrate.userListType3}
          selecteditemid={migrate.parentItemId}
          selecteditemname={selectedUser}
          parentItemId={migrate.parentItemId}
          childItemId={migrate.childItemId}
          userMigrate={migrate.confirm}
          userMigrateLabel="Are You Sure, You Want to Migrate?"
        />
      )}

      {mode === "statistics" && selectedUser && (
        <ViewStatistics
          selectItem={selectedUser}
          onClose={goList}
          title="Users"
          showUnlimited
          translate={props.translate}
        />
      )}

      {mode === "list" && (
        <>
          <Grid container className="breadcrumb-row">
            <ContractTypeTabs
              value={tabValue}
              onChange={handleTabChange}
              translate={props.translate}
              themecolors={themecolors}
            />
            <Grid
              item
              xs={7}
              sm={7}
              className="users-list-actions"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexWrap: "nowrap",
                gap: 8,
                minWidth: 0,
                paddingLeft: 16,
              }}
            >
              <div className="users-list-actions__field">
                <TextField
                  fullWidth
                  label={props.translate("search")}
                  value={searchInput}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="users-list-actions__field">
                <SearchDropdown
                  key={contractTypeFromTab(tabValue)}
                  apiUrl={(search) =>
                    `/api/accounts/getlimiteddata?search=${search}&all=true&userId=${logInUser?.id}&contractType=${contractTypeFromTab(
                      tabValue,
                    )}`
                  }
                  value={listState.cnValue}
                  onChange={(val) =>
                    setListState((prev) => ({
                      ...prev,
                      cnValue: val || null,
                      page: 1,
                    }))
                  }
                  label={props.translate("searchAccounts")}
                />
              </div>
              <div className="users-list-actions__btn">
                <UsersFilterPanel
                  translate={props.translate}
                  themecolors={themecolors}
                  userType={logInUser.userType}
                  roles={roles}
                  statusFilter={filters.statusFilter}
                  statusRoleFilter={filters.statusRoleFilter}
                  countChecked={filters.countChecked}
                  countCheckedRole={filters.countCheckedRole}
                  onUserTypeChange={handleUserTypeFilterChange}
                  onRoleChange={handleRoleFilterChange}
                  onClear={clearFilters}
                />
              </div>
              <div className="users-list-actions__btn">
                <Button
                  disabled={!checkPrivileges("userCreate")}
                  aria-label="Create"
                  size="small"
                  color="inherit"
                  onClick={() => setMode("create")}
                  id="createButton"
                >
                  {props.translate("create")}
                </Button>
              </div>
              <div className="users-list-actions__btn">
                <ExportImportMenu
                  fileName="uploadAccount"
                  isUploadAccount
                  isServices
                  themecolors={themecolors}
                  translate={props.translate}
                  title={props.translate("reportExport")}
                  downloadType="users"
                  DataLength={listState.tableData.length > 0}
                  closedModel={goList}
                  dispatch={dispatch}
                  contractType={contractTypeFromTab(tabValue)}
                  noPdf
                />
              </div>
            </Grid>
          </Grid>
          {listState.loading && !listState.tableData.length ? (
            <Loader />
          ) : (
            <Table
              rows={listState.tableData}
              pagination={setPagination(listState.pagData, listState.pageSize, listState.tableData.length)}
              handleChangeRowsPerPage={(pageSize) =>
                setListState((prev) => ({ ...prev, pageSize, page: 1 }))
              }
              handleChangePage={(page) => setListState((prev) => ({ ...prev, page }))}
              SearchItem={(value) => {
                setSearchInput(value);
                setListState((prev) => ({ ...prev, itemSearch: value, page: 1 }));
              }}
              onClick={(item) => {
                setSelectedUser(pickRow(item));
                setCreatedFlags({ spCreated: false, aaCreated: false });
                setMode("detail");
              }}
              onDelete={(item) => setDeleteTarget(pickRow(item))}
              loginAsUser={(data) => {
                const user = pickRow(data);
                if (user?.logInAsUserState) {
                  setLoginUser(user);
                  setLoginAsKey((k) => k + 1);
                }
              }}
              showStatistics={(item) => {
                setSelectedUser(pickRow(item));
                setMode("statistics");
              }}
              loginAsUserhandleChange={(id, value) =>
                setListState((prev) => ({
                  ...prev,
                  tableData: prev.tableData.map((row) =>
                    row.id === id ? { ...row, logInAsUserState: value } : row,
                  ),
                }))
              }
              ServerSetting={ServerSetting}
              changeContract={async (item, event) => {
                event.stopPropagation();
                const user = pickRow(item);
                const admins = await listAccountAdmins(logInUser.id, rolesRef.current);
                setSelectedUser(user);
                setMigrate({
                  parentItemId: user.id,
                  childItemId: "",
                  userListType3: admins,
                  confirm: false,
                });
                setMode("changeContract");
              }}
              btnName={props.translate("create")}
              onCreate={() => setMode("create")}
              btnName1="users"
              onBinaryView={() => props.navigate("/users/view")}
              translate={props.translate}
              logInUser={logInUser}
              openUserStatusDialog={(event, item) => {
                event.stopPropagation();
                setStatusTarget(pickRow(item));
              }}
              btnView={logInUser.userType === -1 ? props.translate("view") : ""}
              viewButton={logInUser.userType === -1}
              isEditable={checkPrivileges("accountUpdate")}
              themecolors={themecolors}
              isRowIcons
              isCursoPointer
              filterDropdown
              hasAccessOfChangeStatus={logInUser.userType === -1}
              onChangeStatus={(item) =>
                setChildStatus({ open: true, mode: false, isSubmit: false, user: pickRow(item) })
              }
              hasAccessOfUpdate={checkPrivileges("userUpdate")}
              hasAccessOfDelete={checkPrivileges("userDelete")}
              rowDefinition={rowDefinition}
              userStatus={listState.tableData.map((user) => user.disabled)}
            />
          )}
        </>
      )}

      {loginUser && (
        <LoginAsUser key={loginAsKey} selectedUser={loginUser} />
      )}

      {deleteTarget && (
        <ConfirmDialoag
          translate={props.translate}
          onCancel={() => setDeleteTarget(null)}
          onOk={async () => {
            try {
              await deleteUser(deleteTarget);
              toast.success(props.translate("userIsRemoved"));
              setDeleteTarget(null);
              loadUsers();
            } catch (error) {
              toast.error(handleIdentityError(error, props.translate));
            }
          }}
          title={props.translate("areYouWantToDelete")}
          children={deleteTarget.name}
        />
      )}

      {statusTarget && (
        <ConfirmDialoag
          onCancel={() => setStatusTarget(null)}
          onOk={async () => {
            try {
              await activateUser(statusTarget.id, statusTarget.disabled);
              toast.success(`User ${statusTarget.disabled ? "Activated" : "Deactivated"}`);
              setStatusTarget(null);
              loadUsers();
            } catch (error) {
              toast.error(handleIdentityError(error, props.translate));
            }
          }}
          title={
            statusTarget.disabled
              ? "Are You Sure, You Want to Activate"
              : "Are You Sure, You Want to DeActivate"
          }
          children={
            <span>
              <span style={{ fontWeight: "bold" }}>User Name : </span>
              {statusTarget.name}
            </span>
          }
        />
      )}

      {childStatus.open && (
        <CustomDialog
          title="Change Status"
          themecolors={themecolors}
          visable
          translate={props.translate}
          onClose={() => setChildStatus({ open: false, mode: false, isSubmit: false })}
          bodyPadding={10}
          hideDragIcon
          fullWidth
          headerActions={
            <Button
              onClick={async () => {
                try {
                  const user = childStatus.user;
                  if (user.disabled) await enableUser(user.id, childStatus.mode);
                  else await disableUser(user.id, childStatus.mode);
                  toast.success(
                    props.translate(
                      childStatus.mode
                        ? user.disabled
                          ? "allChildEnabledSuccess"
                          : "allChildDisabledSuccess"
                        : user.disabled
                          ? "userEnabled"
                          : "userDisabled",
                    ),
                  );
                  setChildStatus({ open: false, mode: false, isSubmit: false });
                  loadUsers();
                } catch (error) {
                  toast.error(handleIdentityError(error, props.translate));
                }
              }}
            >
              {props.translate("Submit")}
            </Button>
          }
          isVisableBtn
          noFullScreen
        >
          <Checkbox
            name="DocCheckbox"
            color="primary"
            checked={childStatus.mode}
            value
            onChange={(e) =>
              setChildStatus((prev) => ({ ...prev, mode: e.target.checked, isSubmit: true }))
            }
            label={props.translate(
              childStatus.user?.disabled ? "allChildEnable" : "allChildDisable",
            )}
          />
        </CustomDialog>
      )}
    </div>
  );
};

export const UsersList = withRoutes(UsersListInner);
export default UsersList;
